import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Users, Shield, User, Tv } from 'lucide-react';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { FamilyMember } from '@lib/streamFlowApi';
import IPTVDeviceModal from '@components/streamflow/IPTVDeviceModal';

export default function StreamMembers() {
  const navigate = useNavigate();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ display_name: '', family_role: 'member' as const, pin: '' });
  const [iptvFor, setIptvFor] = useState<FamilyMember | null>(null);

  const fetchMembers = async () => {
    const res = await streamFlowApi.listMembers();
    if (res.success) setMembers(res.data || []);
    setLoading(false);
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const data: any = { display_name: formData.display_name, family_role: formData.family_role };
    if (formData.pin) data.pin = formData.pin;

    const res = await streamFlowApi.createMember(data);
    if (res.success) {
      toast.success('Family member added!');
      setShowForm(false);
      setFormData({ display_name: '', family_role: 'member', pin: '' });
      fetchMembers();
    } else {
      toast.error(res.error?.message || 'Failed to add member');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Remove "${name}" from the family?`)) return;
    const res = await streamFlowApi.deleteMember(id);
    if (res.success) {
      toast.success('Member removed');
      fetchMembers();
    } else {
      toast.error(res.error?.message || 'Failed to remove');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Family Members</h1>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm">
          <Plus className="w-4 h-4" /> Add Member
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white border rounded-lg p-6 mb-6 space-y-4">
          <h3 className="font-semibold">New Family Member</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input type="text" required value={formData.display_name} onChange={e => setFormData(d => ({ ...d, display_name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="e.g., Dad, Mom, Alex" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Role</label>
              <select value={formData.family_role} onChange={e => setFormData(d => ({ ...d, family_role: e.target.value as any }))}
                className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">PIN (optional, for shared TVs)</label>
              <input type="text" value={formData.pin} onChange={e => setFormData(d => ({ ...d, pin: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="4-8 digit PIN" maxLength={8} />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm">Add Member</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-gray-500 text-center py-8">Loading...</p>
      ) : members.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded-lg">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No family members yet. Add your first member to start streaming.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {members.map(m => (
            <div key={m.id} className="bg-white border rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold">
                  {m.display_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{m.display_name}</p>
                    {m.family_role === 'admin' && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                        <Shield className="w-3 h-3" /> Admin
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">Added {new Date(m.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIptvFor(m)}
                  className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                  title="IPTV devices (Tivimate / Smarters / VLC)"
                >
                  <Tv className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(m.id, m.display_name)} className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {iptvFor && (
        <IPTVDeviceModal member={iptvFor} onClose={() => setIptvFor(null)} />
      )}
    </div>
  );
}
